<script setup lang="ts">
import type { Gesuch } from '@/models/Gesuch'
import { onMounted, ref, useTemplateRef } from 'vue'
import { buildUrl } from '@/helpers/api'
import VuePdfEmbed from 'vue-pdf-embed'

const {
    gesuch = null,
    loading = false,
    type = null,
} = defineProps<{
    readOnly?: boolean
    gesuch?: Gesuch | null
    loading?: boolean
    type: string
}>()

const numPages = ref(0)
const page = ref(1)
const loadedRatio = ref(0)
const errorMsg = ref(false)
const src = ref('')

const pdfRef = useTemplateRef<typeof VuePdfEmbed | null>('pdfRef')

onMounted(() => {
    setTimeout(
        () =>
            (src.value = buildUrl('/gesuch/getFoerderDok', { gesuch_id: gesuch?.id, type: type })),
        1,
    )
})

function download() {
    const url = buildUrl('/gesuch/getFoerderDok', {
        gesuch_id: gesuch?.id,
        type: type,
    })
    window.open(url, 'apollon-foerderdok-dl')
}

function error() {
    errorMsg.value = true
    loadedRatio.value = -1
}

function handleDocumentRender() {
    numPages.value = pdfRef.value?.pageCount
}
</script>

<template>
    <v-container fluid>
        <v-card class="centered-card" variant="flat">
            <v-card-actions>
                <v-btn
                    v-if="!errorMsg"
                    class="hidden-sm-and-up"
                    variant="flat"
                    :loading="loading"
                    @click="download"
                    icon="fas fa-download"
                >
                </v-btn>
                <v-btn
                    v-if="!errorMsg"
                    variant="flat"
                    class="hidden-xs"
                    :loading="loading"
                    @click="download"
                    prepend-icon="fas fa-download"
                >
                    {{ type ? $t(type) : '' }} {{ $t('download') }}
                </v-btn>
                <v-spacer />
                <v-btn
                    variant="flat"
                    class="mt-2"
                    color="action"
                    :loading="loading"
                    @click="$emit('next', null)"
                    append-icon="fas fa-arrow-right"
                >
                    {{ $t('next_step') }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-alert
            class="centered-card"
            v-if="errorMsg"
            type="info"
            :icon="false"
            border="start"
            colored-border
            variant="outlined"
        >
            <span>{{ $t('no_file') }}</span>
        </v-alert>
        <v-card
            v-if="!errorMsg"
            class="mt-2 pa-0 centered-card"
            :loading="loadedRatio >= 0 && loadedRatio < 1"
            elevation="10"
            max-width="700pt"
        >
            <v-pagination
                v-if="numPages > 1"
                v-model="page"
                :length="numPages"
                circle
            ></v-pagination>
            <v-card-text>
                <VuePdfEmbed
                    :source="src"
                    ref="pdfRef"
                    :page="page"
                    @progress="(event) => (loadedRatio = event.total)"
                    @loading-failed="error"
                    @rendering-failed="error"
                    @printing-failed="error"
                    @internal-link-clicked="numPages = $event"
                    @rendered="handleDocumentRender"
                ></VuePdfEmbed>
            </v-card-text>
            <v-pagination
                v-if="numPages > 1"
                v-model="page"
                :length="numPages"
                circle
            ></v-pagination>
        </v-card>
    </v-container>
</template>
